<script setup lang="ts">
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Card, Column, DataTable, InputText, type DataTableRowDoubleClickEvent } from 'primevue';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useMappingTraceStore } from 'shared/products/mapping/stores/mappingTraceStore';
import { useRefreshMappingTraceStore } from '../commands/refreshMappingTraceStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { useRefreshPackageTypeStore } from '@/packages/commands/refreshPackageTypeStore';
import { useRefreshPackagingStore } from '@/packages/commands/refreshPackagingStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import EditMappingTraceDialog from './EditMappingTraceDialog.vue';

const selectedColumnsStorageKey = 'mapping-traces-table-columns';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useMappingTraceStore();
const refreshMappingTraceStore = useRefreshMappingTraceStore();
const refreshProductStore = useRefereshProductStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshPackagingStore = useRefreshPackagingStore();
const productStore = useProductStore();
const productGroupStore = useProductGroupStore();
const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();

const selectedRow = ref<{ id: string } | null>(null);
const editDialog = useTemplateRef('edit-dialog');

const columnDefinitions = computed<ColumnField[]>(() => [
  { field: 'createdAtUtc', header: translations.value.products.mapping.created, filterType: 'text' },
  { field: 'sourceImportRowId', header: translations.value.products.mapping.importRow, filterType: 'text' },
  { field: 'sourceProduct', header: translations.value.products.mapping.sourceProduct, filterType: 'text' },
  { field: 'sourceData', header: translations.value.products.mapping.sourceData, filterType: 'text' },
  { field: 'result', header: translations.value.products.mapping.result, filterType: 'text' },
  { field: 'correction', header: translations.value.products.mapping.correction, filterType: 'text' },
]);

const defaultFields = ['createdAtUtc', 'sourceImportRowId', 'sourceProduct', 'result', 'correction'];

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({ storageKey: selectedColumnsStorageKey, columns: columnDefinitions, defaultFields });

function labelForProduct(id: string | null): string | null {
  if (!id) return null;
  return productStore.getById(id)?.name ?? id;
}

function labelForGroup(id: string | null): string | null {
  if (!id) return null;
  return productGroupStore.getById(id)?.name ?? id;
}

function labelForPackageType(id: string | null): string | null {
  if (!id) return null;
  return packageTypeStore.getById(id)?.name ?? id;
}

function labelForPackaging(id: string | null): string | null {
  if (!id) return null;
  return packagingStore.getById(id)?.name ?? id;
}

const rows = computed(() => {
  return store.sortedByNewest.map((item) => ({
    id: item.id,
    createdAtUtc: item.createdAtUtc,
    sourceImportRowId: item.sourceImportRowId,
    sourceProduct: item.sourceProductName,
    sourceData: [
      item.sourceProductGroupName ? `Group: ${item.sourceProductGroupName}` : null,
      item.sourceCode ? `Code: ${item.sourceCode}` : null,
      item.sourceWidth !== null || item.sourceHeight !== null || item.sourceLength !== null
        ? `Dims: ${item.sourceWidth ?? '-'} / ${item.sourceHeight ?? '-'} / ${item.sourceLength ?? '-'}`
        : null,
      item.sourceMoisture !== null ? `Moisture: ${item.sourceMoisture}` : null,
    ].filter((part): part is string => !!part).join(' | '),
    result: [
      item.matchedProfileName ? `Profile: ${item.matchedProfileName}` : null,
      item.resolvedProductName ? `Product: ${item.resolvedProductName}` : null,
      item.resolvedPackageTypeName ? `Package type: ${item.resolvedPackageTypeName}` : null,
      item.resolvedPackagingName ? `Packaging: ${item.resolvedPackagingName}` : null,
    ].filter((part): part is string => !!part).join(' | '),
    correction: [
      item.correctedAtUtc ? `Corrected ${item.revision}` : null,
      item.correctedBy ? `By ${item.correctedBy}` : null,
      item.correctionNote ? item.correctionNote : null,
    ].filter((part): part is string => !!part).join(' | '),
  }));
});

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => rows.value.length,
});

const selected = computed(() => selectedRow.value?.id);

clearCorruptDataTableState('mapping-traces-table-state');

onMounted(() => {
  refreshProductStore.initialize();
  refreshProductGroupStore.initialize();
  refreshPackageTypeStore.initialize();
  refreshPackagingStore.initialize();
  refreshMappingTraceStore.initialize();
});

const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

function onCorrect() {
  if (!selected.value) return;
  editDialog.value?.show(selected.value);
}

function onRowDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const mappingTrace = ev.data as { id: string };
  selectedRow.value = { id: mappingTrace.id };
  editDialog.value?.show(mappingTrace.id);
}

function onSaved(id: string) {
  selectedRow.value = { id };
}
</script>

<template>
  <Card>
    <template #title>
      <div class="title-row">
        <span>Mapping trace</span>
        <div class="actions">
          <span v-if="hasActiveFilters && filteredCount < rows.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ rows.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
          <Button :label="translations.generic.edit" outlined :disabled="!selected" @click="onCorrect"></Button>
          <InputText v-model="filters.global.value" :placeholder="translations.generic.search"></InputText>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:selection="selectedRow"
        v-model:filters="filters"
        v-model:multiSortMeta="multiSortMeta"
        :class="sortBadgeClass"
        :value="rows"
        data-key="id"
        selectionMode="single"
        sort-mode="multiple"
        stateStorage="local"
        stateKey="mapping-traces-table-state"
        filterDisplay="menu"
        @filter="onFilter"
        @row-dblclick="onRowDoubleClick"
      >
        <Column v-for="(col, index) of selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column column-key="column-selector" class="w-12 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditMappingTraceDialog ref="edit-dialog" @saved="onSaved"></EditMappingTraceDialog>
</template>

<style scoped>
.title-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
